import { useEffect, useState } from 'react';
import { ApiFilterOperation, ResultDto } from '@eas/common-web';
import { Judge } from '@models';
import { Agenda, EsFileState, EvidenceApiUrl } from '@enums';

export type JudgeFileCount = { id: string; name: string; value: number };

const filter = (field: string, value: string) => ({
  field,
  value,
  operation: ApiFilterOperation.EQ,
});

async function list<T>(
  url: string,
  filters: ReturnType<typeof filter>[],
  size = 100
) {
  const response = await fetch(`${url}/list`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ filters, size }),
  });
  if (!response.ok) throw new Error(`Failed to load ${url}`);
  return (await response.json()) as ResultDto<T>;
}

async function countOfficeFiles(field: string, id: string) {
  const result = await list<unknown>(
    EvidenceApiUrl.ES_FILES,
    [
      filter('agenda.id', Agenda.COURT),
      filter('state.id', EsFileState.JUDGE_OFFICE),
      filter(field, id),
    ],
    0
  );
  return result.count;
}

export function useCourtFileCounts(judgeId?: string, assistantRoleId?: string) {
  const [judgeCount, setJudgeCount] = useState<number>();
  const [assistantCount, setAssistantCount] = useState<number>();
  const [assistantJudges, setAssistantJudges] = useState<JudgeFileCount[]>([]);

  useEffect(() => {
    let active = true;
    setJudgeCount(undefined);
    if (judgeId) {
      countOfficeFiles('judge.id', judgeId)
        .then((count) => active && setJudgeCount(count))
        .catch(() => active && setJudgeCount(undefined));
    }
    return () => {
      active = false;
    };
  }, [judgeId]);

  useEffect(() => {
    let active = true;
    setAssistantCount(undefined);
    setAssistantJudges([]);
    if (assistantRoleId) {
      countOfficeFiles('assistantUserRoles.id', assistantRoleId)
        .then((count) => active && setAssistantCount(count))
        .catch(() => active && setAssistantCount(undefined));
      list<Judge>(EvidenceApiUrl.JUDGES, [
        filter('assistantUserRoles.id', assistantRoleId),
      ])
        .then(async (result) => {
          const judges = await Promise.all(
            result.items.map(async (judge) => ({
              id: judge.id,
              name: judge.name,
              value: await countOfficeFiles('judge.id', judge.id),
            }))
          );
          if (active) setAssistantJudges(judges);
        })
        .catch(() => active && setAssistantJudges([]));
    }
    return () => {
      active = false;
    };
  }, [assistantRoleId]);

  return { judgeCount, assistantCount, assistantJudges };
}
